import { useState } from "react";
import { Button } from "../ui/Button";
import { Field, FieldNote, Form, NumInput } from "../ui/Field";
import { Modal } from "../ui/Overlay";
import { Notice } from "../ui/Notice";
import { useT } from "../../lib/i18n";
import type { Level } from "../../lib/model";

/**
 * 批量设置建筑数量 / 星级。
 *
 * 50 关逐格改 N/M 要点 100 次，而实际配法几乎总是分段的
 * （「11~20 关都改成 6 栋」）。所以这里按关卡区间改，不是全表覆盖。
 *
 * 两个字段都可以留空——留空表示这一项不动。批量操作最怕的是
 * 「我只想改星级，结果建筑数量也被写成了默认值」。
 */
export function BatchGridDialog({
  levels, onClose, onSubmit,
}: {
  levels: Level[];
  onClose: () => void;
  onSubmit: (v: { from: number; to: number; N: number | null; M: number | null }) => void;
}) {
  const t = useT();
  const [from, setFrom] = useState<number | null>(1);
  const [to, setTo] = useState<number | null>(levels.length);
  const [N, setN] = useState<number | null>(null);
  const [M, setM] = useState<number | null>(null);

  const lo = from ?? 1;
  const hi = to ?? levels.length;
  const hit = levels.filter((l) => l.id >= lo && l.id <= hi).length;
  const nothing = N == null && M == null;
  const badRange = lo > hi || hit === 0;

  return (
    <Modal
      title={t("dlg.batchGrid")}
      width={520}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button tone="danger" disabled={nothing || badRange} onClick={() => onSubmit({ from: lo, to: hi, N, M })}>
            {t("common.apply")}
          </Button>
        </>
      }
    >
      <Form>
        <FieldNote full><Notice tone="warning">{t("dlg.batchGrid.warn")}</Notice></FieldNote>

        <Field label={t("flt.range")}>
          {/* 起止两个框是一个整体，贴紧（gap-1.5）；命中数是对它的注解，由 Field 的 gap-2 隔开 */}
          <span className="flex items-center gap-1.5">
            <NumInput value={from} className="w-[80px]" onChange={setFrom} />
            <span className="text-ink-placeholder">~</span>
            <NumInput value={to} className="w-[80px]" onChange={setTo} />
          </span>
          <span className="text-xs text-ink-placeholder">
            {badRange ? <span className="text-danger">{t("dlg.batchGrid.badRange")}</span> : t("dlg.batchGrid.hit", { n: hit })}
          </span>
        </Field>

        <Field label={t("level.col.N")}>
          <NumInput value={N} className="w-[80px]" placeholder={t("dlg.batchGrid.keep")} onChange={setN} />
        </Field>
        <Field label={t("level.col.M")} hint={t("dlg.batchGrid.keepHint")}>
          <NumInput value={M} className="w-[80px]" placeholder={t("dlg.batchGrid.keep")} onChange={setM} />
        </Field>
      </Form>
    </Modal>
  );
}
